import React, { useCallback, useEffect, useState } from 'react'
import Modal from 'react-responsive-modal'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import { Input, Select } from '../../components/ModalInputs'
import Table from '../../components/Table'
import Button from '../../components/Button'
import Loader from '../../components/Loader'
import ModalFooter from '../../components/ModalFooter/ModalFooter'
import Confirmation from '../../components/Confirmation/Confirmation'
import { BiTrash } from 'react-icons/bi'
import { IoIosAdd } from 'react-icons/io'
import {
  getApplicantGroupItem,
  createApplicantGroup,
  updateApplicantGroup,
} from '../../actions/applicantGroup'
import styles from './styles.module.scss'

const STREAM_OPTIONS = [
  { value: 0, name: 'Основной набор' },
  { value: 1, name: 'Дополнительный набор' },
]

const CLASS_OPTIONS = [
  { value: 5, name: '5' },
  { value: 6, name: '6' },
  { value: 7, name: '7' },
  { value: 8, name: '8' },
]

const makeDefault = (schoolYear) => ({
  number: '',
  name: '',
  school_year: schoolYear || 0,
  stream: 0,
  class_year: 5,
  closed: 0,
  description: '',
  exam_city: 0,
  exam_place: 0,
  arrival: '',
  exam_1: { enterexam1name: '', enterexam1date: '', pass1grade: 0, exam1colname: '' },
  exam_2: { enterexam2name: '', enterexam2date: '', pass2grade: 0, exam2colname: '' },
  exam_3: { enterexam3name: '', enterexam3date: '', pass3grade: 0, exam3colname: '' },
  exam_4: { enterexam4name: '', enterexam4date: '', pass4grade: 0, exam4colname: '' },
  exam_5: { enterexam5name: '', enterexam5date: '', pass5grade: 0, exam5colname: '' },
  exam_6: { enterexam6name: '', enterexam6date: '', pass6grade: 0, exam6colname: '' },
  physical_standarts: [],
  applicants: [],
})

const validationSchema = Yup.object({
  name: Yup.string().required('Обязательное поле'),
})

export default function ApplicantGroupModal({ id, params, closeModal }) {
  const dispatch = useDispatch()
  const { item, loadingItem, physical_training, aplicantList } = useSelector(state => state.applicant_group)
  const { schoolYears } = useSelector(state => state.common)
  const { list: examCities } = useSelector(state => state.exam_city)

  const addMode = id === 'add'
  const isOpen = id !== null

  const [confirmClose, setConfirmClose] = useState(false)
  const [addNormId, setAddNormId] = useState('')
  const [addApplicantId, setAddApplicantId] = useState('')

  useEffect(() => {
    if (isOpen && !addMode && id) {
      dispatch(getApplicantGroupItem(id))
    }
  }, [id])

  const handleClose = useCallback(() => setConfirmClose(true), [])
  const acceptClose = useCallback(() => { closeModal(); setConfirmClose(false) }, [closeModal])
  const rejectClose = useCallback(() => setConfirmClose(false), [])

  const initialValues = addMode
    ? makeDefault(params?.school_year)
    : (item?.name ? item : makeDefault(params?.school_year))

  const { values, errors, touched, submitCount, handleChange, setFieldValue, handleSubmit } = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (vals) => {
      const payload = {
        ...vals,
        physical_standarts: (vals.physical_standarts || []).map(({ iid, sorting }) => ({ iid, sorting })),
        applicants: (vals.applicants || []).map(a => a.xp_key ?? a),
      }
      if (addMode) {
        await dispatch(createApplicantGroup(payload, params))
      } else {
        await dispatch(updateApplicantGroup(id, payload, params))
      }
      closeModal()
    },
  })

  const error = (field) => (touched[field] || submitCount) && errors[field]

  const availableNorms = (physical_training || []).filter(
    n => !(values.physical_standarts || []).find(s => s.iid === n.iid)
  )
  const availableApplicants = (aplicantList || []).filter(
    a => !(values.applicants || []).find(ex => (ex.xp_key ?? ex) === a.xp_key)
  )

  const handleAddNorm = () => {
    if (!addNormId) return
    const norm = physical_training.find(n => String(n.iid) === String(addNormId))
    if (!norm) return
    const sorting = ((values.physical_standarts || []).length + 1) * 10
    setFieldValue('physical_standarts', [
      ...(values.physical_standarts || []),
      { iid: norm.iid, name: norm.iname, sorting, id: crypto.randomUUID() },
    ])
    setAddNormId('')
  }

  const handleRemoveNorm = (iid) => {
    setFieldValue('physical_standarts', (values.physical_standarts || []).filter(s => s.iid !== iid))
  }

  const handleAddApplicant = () => {
    if (!addApplicantId) return
    const applicant = aplicantList.find(a => String(a.xp_key) === String(addApplicantId))
    if (!applicant) return
    setFieldValue('applicants', [...(values.applicants || []), applicant])
    setAddApplicantId('')
  }

  const handleRemoveApplicant = (xpKey) => {
    setFieldValue('applicants', (values.applicants || []).filter(a => (a.xp_key ?? a) !== xpKey))
  }

  const updateNormSorting = (iid, sorting) => {
    setFieldValue('physical_standarts', (values.physical_standarts || []).map(s =>
      s.iid === iid ? { ...s, sorting: Number(sorting) } : s
    ))
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        showCloseIcon={false}
        classNames={{ modal: styles.modal }}
        closeOnEsc
        closeOnOverlayClick
        animationDuration={1}
      >
        <div className={styles.header}>
          {addMode ? 'Новая группа испытания' : 'Группа испытания'}
        </div>

        {loadingItem && !addMode ? (
          <div className={styles.loaderWrap}><Loader /></div>
        ) : (
          <div className={styles.content}>
            <Tabs>
              <TabList className={styles.tabsBlock}>
                <Tab className={styles.tab} selectedClassName={styles.activeTab}>Общие данные</Tab>
                <Tab className={styles.tab} selectedClassName={styles.activeTab}>Расписание экзаменов</Tab>
                <Tab className={styles.tab} selectedClassName={styles.activeTab}>ОФП и состав</Tab>
              </TabList>

              <TabPanel>
                <div className={styles.inputsRow}>
                  <Input
                    label='Номер'
                    name='number'
                    value={values.number || ''}
                    onChange={handleChange}
                    style={{ width: '120px' }}
                  />
                  <Input
                    label='Наименование'
                    name='name'
                    value={values.name || ''}
                    onChange={handleChange}
                    error={error('name')}
                    style={{ width: '320px' }}
                  />
                </div>
                <div className={styles.inputsRow}>
                  <Select
                    label='Год поступления'
                    name='school_year'
                    value={values.school_year}
                    onChange={e => setFieldValue('school_year', Number(e.target.value))}
                  >
                    {schoolYears?.map(el => (
                      <option key={el.xp_key} value={el.xp_key}>{el.name}</option>
                    ))}
                  </Select>
                  <Select
                    label='Набор'
                    name='stream'
                    value={values.stream}
                    onChange={e => setFieldValue('stream', Number(e.target.value))}
                  >
                    {STREAM_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.name}</option>
                    ))}
                  </Select>
                  <Select
                    label='Класс'
                    name='class_year'
                    value={values.class_year}
                    onChange={e => setFieldValue('class_year', Number(e.target.value))}
                  >
                    {CLASS_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.name}</option>
                    ))}
                  </Select>
                  <Select
                    label='Статус'
                    name='closed'
                    value={values.closed}
                    onChange={e => setFieldValue('closed', Number(e.target.value))}
                  >
                    <option value={0}>Открыта</option>
                    <option value={1}>Закрыта</option>
                  </Select>
                </div>
                <div className={styles.inputsRow}>
                  <Select
                    label='Город экзамена'
                    name='exam_city'
                    value={values.exam_city}
                    onChange={e => setFieldValue('exam_city', Number(e.target.value))}
                  >
                    <option value={0}></option>
                    {examCities?.map(c => (
                      <option key={c.xp_key} value={c.xp_key}>{c.name}</option>
                    ))}
                  </Select>
                  <Input
                    label='Место проведения (id)'
                    name='exam_place'
                    type='number'
                    value={values.exam_place || ''}
                    onChange={e => setFieldValue('exam_place', Number(e.target.value))}
                    style={{ width: '160px' }}
                  />
                  <Input
                    label='Дата прибытия'
                    name='arrival'
                    type='date'
                    value={values.arrival || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className={styles.inputsRow}>
                  <Input
                    label='Описание'
                    name='description'
                    value={values.description || ''}
                    onChange={handleChange}
                    style={{ width: '520px' }}
                  />
                </div>
              </TabPanel>

              <TabPanel>
                <Table className={styles.examsTable}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Название экзамена</th>
                      <th>Дата</th>
                      <th>Проходной балл</th>
                      <th>Название колонки</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4, 5, 6].map(n => {
                      const key = `exam_${n}`
                      const examVals = values[key] || {}
                      return (
                        <tr key={n}>
                          <td>{n}</td>
                          <td>
                            <input
                              value={examVals[`enterexam${n}name`] || ''}
                              onChange={e => setFieldValue(`${key}.enterexam${n}name`, e.target.value)}
                              className={styles.tableInput}
                            />
                          </td>
                          <td>
                            <input
                              type='date'
                              value={examVals[`enterexam${n}date`] || ''}
                              onChange={e => setFieldValue(`${key}.enterexam${n}date`, e.target.value)}
                              className={styles.tableInput}
                            />
                          </td>
                          <td>
                            <input
                              type='number'
                              value={examVals[`pass${n}grade`] || ''}
                              onChange={e => setFieldValue(`${key}.pass${n}grade`, Number(e.target.value))}
                              className={styles.tableInput}
                              style={{ width: '80px' }}
                            />
                          </td>
                          <td>
                            <input
                              value={examVals[`exam${n}colname`] || ''}
                              onChange={e => setFieldValue(`${key}.exam${n}colname`, e.target.value)}
                              className={styles.tableInput}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </TabPanel>

              <TabPanel>
                <div className={styles.tab3}>
                  <div className={styles.tab3Section}>
                    <div className={styles.tab3Title}>Нормативы ОФП</div>
                    <div className={styles.tab3AddRow}>
                      <select
                        value={addNormId}
                        onChange={e => setAddNormId(e.target.value)}
                        className={styles.tab3Select}
                      >
                        <option value=''>— выбрать норматив —</option>
                        {availableNorms.map(n => (
                          <option key={n.iid} value={n.iid}>{n.iname}</option>
                        ))}
                      </select>
                      <Button mode='bordered' type='button' onClick={handleAddNorm}>
                        <IoIosAdd size={18} />Добавить
                      </Button>
                    </div>
                    <Table className={styles.tab3Table}>
                      <thead>
                        <tr>
                          <th>Норматив</th>
                          <th style={{ width: '90px' }}>Порядок</th>
                          <th style={{ width: '1.5rem' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(values.physical_standarts || []).map(s => (
                          <tr key={s.iid ?? s.id}>
                            <td>{s.name}</td>
                            <td>
                              <input
                                type='number'
                                value={s.sorting}
                                onChange={e => updateNormSorting(s.iid, e.target.value)}
                                className={styles.tableInput}
                                style={{ width: '70px' }}
                              />
                            </td>
                            <td>
                              <i className={styles.trash} onClick={() => handleRemoveNorm(s.iid)}>
                                <BiTrash />
                              </i>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <div className={styles.tab3Section}>
                    <div className={styles.tab3Title}>Состав группы</div>
                    <div className={styles.tab3AddRow}>
                      <select
                        value={addApplicantId}
                        onChange={e => setAddApplicantId(e.target.value)}
                        className={styles.tab3Select}
                      >
                        <option value=''>— выбрать претендента —</option>
                        {availableApplicants.map(a => (
                          <option key={a.xp_key} value={a.xp_key}>
                            {[a.child_lastname, a.child_firstname, a.child_midname].filter(Boolean).join(' ')}
                          </option>
                        ))}
                      </select>
                      <Button mode='bordered' type='button' onClick={handleAddApplicant}>
                        <IoIosAdd size={18} />Добавить
                      </Button>
                    </div>
                    <Table className={styles.tab3Table}>
                      <thead>
                        <tr>
                          <th>ФИО</th>
                          <th>Пол</th>
                          <th>Дата рождения</th>
                          <th style={{ width: '1.5rem' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(values.applicants || []).map((a, idx) => {
                          const xpKey = a.xp_key ?? a
                          const info = typeof a === 'object' ? a : (aplicantList || []).find(x => x.xp_key === a)
                          const fio = info
                            ? [info.child_lastname, info.child_firstname, info.child_midname].filter(Boolean).join(' ')
                            : String(xpKey)
                          return (
                            <tr key={xpKey ?? idx}>
                              <td>{fio}</td>
                              <td>{info?.sex || ''}</td>
                              <td>{info?.child_birth || ''}</td>
                              <td>
                                <i className={styles.trash} onClick={() => handleRemoveApplicant(xpKey)}>
                                  <BiTrash />
                                </i>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </TabPanel>
            </Tabs>
          </div>
        )}

        <ModalFooter handleSubmit={handleSubmit} closeModal={handleClose} />
      </Modal>

      {confirmClose && (
        <Confirmation
          title='Закрыть без сохранения изменений?'
          acceptConfirmation={acceptClose}
          rejectConfirmation={rejectClose}
        />
      )}
    </>
  )
}
